import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import Modal from '../Modal';
import RateLine from '../RateLine';
import TextArea from '../TextArea';
import Button from '../Button';

import styles from '../../../styles/views/components/WriterRatingModal.module.scss';
import getRatingData from './getRatingData';
import getTitleKey from './getTitleKey';

const WriterRatingModal = ({
  isShown,
  ratings,
  ratingComment,
  handleCommentInput,
  handleRatingChange,
  onClose,
  onRate,
  bookStructure,
  ratingType,
  callback,
  editorName,
}) => {
  const isSomeRatingEmpty = useMemo(() => ratings.some((rating) => rating.value === 0), [ratings]);
  const ratingsData = useMemo(() => getRatingData(bookStructure, ratingType), [bookStructure, ratingType]);

  return (
    <Modal
      isShown={isShown}
      onClose={onClose}
      modalExtraClass={styles.modal}
    >
      <div className={styles.container}>
        <h2>{t(`writerRatingModal.${ratingType}.title`, { ns: i18next.Namespace.COMMON })}</h2>
        <hr />
        <div className={styles.ratingSection}>
          <p>{t(getTitleKey(bookStructure, ratingType), { ns: i18next.Namespace.COMMON, editorName })}</p>
          {!isEmpty(ratingsData) && ratingsData.map((rating) => (
            <div
              key={rating.name}
              className={styles.ratingItem}
              data-html
              data-tip={t(rating.description, { ns: i18next.Namespace.COMMON })}
            >
              <div>
                <h3>{t(rating.label, { ns: i18next.Namespace.COMMON })}</h3>
                <p>{t(rating.message, { ns: i18next.Namespace.COMMON })}</p>
              </div>
              <span className={styles.ratingStars}>
                <RateLine
                  key={rating.name}
                  name={rating.name}
                  rate={ratings.find((r) => r.key === rating.name)?.value}
                  onChange={handleRatingChange(rating.name)}
                />
              </span>
            </div>
          ))}
        </div>
        <TextArea
          name="comment"
          label={t('writerRatingModal.comment', { ns: i18next.Namespace.COMMON })}
          value={ratingComment}
          onChange={handleCommentInput}
          maxLength={320}
          extraStyles={{ width: '560px', height: '88px' }}
        />
        <div className={styles.buttonsSection}>
          <Button
            color="gold"
            variant="primary"
            name="complete"
            value={t('button.complete', { ns: i18next.Namespace.COMMON })}
            onClick={() => {
              onRate();
              callback();
            }}
            isDisabled={isSomeRatingEmpty}
            tooltip={isSomeRatingEmpty ? t('tooltips.fillOutAllFields', { ns: i18next.Namespace.COMMON }) : ''}
          />
        </div>
      </div>
    </Modal>
  );
};

WriterRatingModal.propTypes = {
  isShown: PropTypes.bool,
  ratingComment: PropTypes.string,
  ratings: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, value: PropTypes.number }),
  ).isRequired,
  handleCommentInput: PropTypes.func.isRequired,
  handleRatingChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRate: PropTypes.func.isRequired,
  bookStructure: PropTypes.string,
  ratingType: PropTypes.string.isRequired,
  callback: PropTypes.func,
  editorName: PropTypes.string,
};

WriterRatingModal.defaultProps = {
  isShown: false,
  ratingComment: '',
  bookStructure: '',
  callback: () => {},
  editorName: '',
};

export default WriterRatingModal;
